import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Edit, Save, X, ChevronLeft, ChevronRight, Upload, FileSpreadsheet, AlertCircle, Keyboard, Trash2, Plus, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Color mapping matching the user's screenshot legend
// PL, WO, SL, HO all share the same bright sky blue (#29B6F6)
const STATUS_COLORS = {
  P:  { bg: '#219235', text: '#fff', label: 'Present' },
  H:  { bg: '#FFFF00', text: '#333', label: 'Half Day' },
  WO: { bg: '#29B6F6', text: '#333', label: 'Weekly Off' },
  PL: { bg: '#29B6F6', text: '#333', label: 'Planned Leave' },
  SL: { bg: '#29B6F6', text: '#333', label: 'Sick Leave' },
  HO: { bg: '#1565C0', text: '#fff', label: 'Holiday' },
  L:  { bg: '#8fcc5e', text: '#1f3d12', label: 'Late Mark' },
  A:  { bg: '#F7931E', text: '#fff', label: 'Absentee' },
  U:  { bg: '#FF0000', text: '#fff', label: 'Unplanned Leave' },
  UL: { bg: '#FF0000', text: '#fff', label: 'Unplanned Leave' },
};

const ALL_CODES = ['P', 'H', 'WO', 'PL', 'SL', 'HO', 'L', 'A', 'U'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function getDayOfWeek(year, month, day) {
  return new Date(year, month - 1, day).getDay();
}

function calcSummary(days) {
  let p = 0, a = 0, h = 0, l = 0, wo = 0, ul = 0, pl = 0, sl = 0, ho = 0;
  Object.values(days).forEach(code => {
    if (code === 'P') p++;
    else if (code === 'H') h++;
    else if (code === 'A') a++;
    else if (code === 'L') l++;
    else if (code === 'WO') wo++;
    else if (code === 'U' || code === 'UL') ul++;
    else if (code === 'PL') pl++;
    else if (code === 'SL') sl++;
    else if (code === 'HO') ho++;
  });
  return { present: p, halfDay: h, absent: a, lateMarkCount: l, weeklyOff: wo, unplanned: ul, plannedLeave: pl, sickLeave: sl, holiday: ho };
}

export default function MonthlyAttendanceTab({ user = null }) {
  const now = new Date();
  // Only Assistant HR (Sahil, DBK-0014) — or system super_admin — may view the
  // FULL staff attendance table and edit it. Every other user (incl. HR Head)
  // sees only their own row.
  const isAttendanceAdmin =
    user?.role === 'super_admin' ||
    user?.employeeId === 'DBK-0014' ||
    user?.employeeId === 'DBK-0036' ||
    (user?.email || '').toLowerCase() === 'assistant.hr@siddhivinayakinfra.estate' ||
    (user?.email || '').toLowerCase() === 'hr.executive@siddhivinayakinfra.estate';
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeCell, setActiveCell] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [part, setPart] = useState(1); // 1 = full grid (all days), 2 = monthly summary
  const [summaryPeriod, setSummaryPeriod] = useState('monthly'); // daily|weekly|monthly|yearly
  const [yearlyCounts, setYearlyCounts] = useState(null);
  const [yearlyLoading, setYearlyLoading] = useState(false);
  const [markingToday, setMarkingToday] = useState(false);
  // Keyboard cell input state
  const [kbCell, setKbCell] = useState(null); // {empId, day}
  const [kbBuffer, setKbBuffer] = useState('');
  const kbTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  const isCurrentMonth = selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear();
  const todayDay = now.getDate();
  const todayDOW = DAY_NAMES[now.getDay()];

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get(`${API}/hr/monthly-attendance`, {
        params: { month: selectedMonth, year: selectedYear },
        headers: { Authorization: `Bearer ${token}` }
      });
      const rawRecords = res.data.records || [];
      // Privacy: only CMD / DBK-0014 / assistant.hr sees the full table.
      // Every other employee sees ONLY their own attendance row.
      const filtered = (!isAttendanceAdmin && user)
        ? rawRecords.filter(r => {
            // Match by employeeId (most reliable)
            if (user.employeeId && r.employeeId === user.employeeId) return true;
            // Match by name — try both 'name' and 'employeeName' fields the API may return
            const recName = (r.employeeName || r.name || '').toString().trim().toLowerCase();
            const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
            if (userName && recName === userName) return true;
            // Partial match — if user full name is contained in record name
            if (userName.length > 3 && recName.includes(userName)) return true;
            return false;
          })
        : rawRecords;

      // ── Auto-create virtual row for employees not yet in the attendance master list ──
      // (e.g. DM / ADM / CP hierarchy employees whose employeeId is not DBK-00XX)
      const finalRecords = (!isAttendanceAdmin && user && filtered.length === 0)
        ? [{
            employeeId: user.employeeId || user.email || 'self',
            employeeName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Me',
            designation: user.designation || user.role || '',
            days: {},
            month: selectedMonth,
            year: selectedYear,
          }]
        : filtered;

      setRecords(finalRecords);
      const data = {};
      finalRecords.forEach(r => { data[r.employeeId] = { ...r.days }; });
      setEditedData(data);
    } catch (err) {
      toast.error('Failed to fetch attendance data');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, isAttendanceAdmin, user?.employeeId, user?.email]);

  useEffect(() => {
    fetchAttendance();
    setEditMode(false);
    setYearlyCounts(null);
  }, [fetchAttendance]);

  // Fetch yearly counts when "yearly" period selected
  useEffect(() => {
    if (summaryPeriod !== 'yearly') return;
    if (yearlyCounts) return;
    setYearlyLoading(true);
    const token = localStorage.getItem('crm_token');
    axios.get(`${API}/hr/attendance-yearly-summary`, {
      params: { year: selectedYear },
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => {
      setYearlyCounts(r.data.counts);
    }).catch(() => {
      toast.error('Failed to load yearly summary');
    }).finally(() => setYearlyLoading(false));
  }, [summaryPeriod, selectedYear, yearlyCounts]);

  const totalDays = getDaysInMonth(selectedYear, selectedMonth);
  const days = Array.from({ length: totalDays }, (_, i) => i + 1);

  // ---- Period days helpers ----
  const getDaysForPeriod = (period) => {
    if (period === 'monthly') return days;
    if (period === 'daily') {
      // Today's date if in current month, else last day
      const d = (selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1)
        ? now.getDate() : totalDays;
      return [d];
    }
    if (period === 'weekly') {
      // Current week (Sun–Sat)
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      const weekDays = days.filter(d => {
        const dt = new Date(selectedYear, selectedMonth - 1, d);
        return dt >= startOfWeek && dt <= endOfWeek;
      });
      // Fallback: last 7 days of month if no match (past months)
      return weekDays.length > 0 ? weekDays : days.slice(-7);
    }
    return days;
  };

  // ---- Compute code counts from editedData ----
  const computeCodeCounts = (periodDays) => {
    const counts = { P: 0, H: 0, WO: 0, PL: 0, SL: 0, HO: 0, L: 0, A: 0, U: 0 };
    records.forEach(rec => {
      const data = editedData[rec.employeeId] || rec.days || {};
      periodDays.forEach(d => {
        const code = data[String(d)];
        if (code && code in counts) counts[code]++;
      });
    });
    return counts;
  };

  const periodDays = getDaysForPeriod(summaryPeriod);
  const codeCounts = summaryPeriod === 'yearly'
    ? (yearlyCounts || { P: 0, H: 0, WO: 0, PL: 0, SL: 0, HO: 0, L: 0, A: 0, U: 0 })
    : computeCodeCounts(periodDays);

  const handleCellClick = (empId, day) => {
    // Non-admin users: clicking their own cell directly enters edit mode — no separate button needed
    if (!isAttendanceAdmin && !editMode) {
      setEditMode(true);
    }
    if (kbTimerRef.current) clearTimeout(kbTimerRef.current);
    setKbCell(null);
    setKbBuffer('');
    // Show dropdown picker — toggle: click same cell again closes it
    setActiveCell(prev =>
      (prev?.empId === empId && prev?.day === day) ? null : { empId, day }
    );
  };

  // handleStatusSelect must be defined BEFORE handleKbInput (which references it in useCallback deps)
  const handleStatusSelect = useCallback(async (empId, day, status) => {
    const updated = { ...editedData };
    if (!updated[empId]) updated[empId] = {};
    updated[empId][day] = status;
    setEditedData(updated);
    setActiveCell(null);
    try {
      const token = localStorage.getItem('crm_token');
      await axios.put(`${API}/hr/monthly-attendance/update-day`, {
        employeeId: empId, month: selectedMonth, year: selectedYear, day, status
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      toast.error('Failed to save cell');
    }
  }, [editedData, selectedMonth, selectedYear]);

  // ── One-tap Mark Present Today ───────────────────────────────────────────
  const handleMarkPresentToday = useCallback(async () => {
    if (!user || isAttendanceAdmin) return;
    const empId = user.employeeId || user.email || 'self';
    const month  = now.getMonth() + 1;
    const year   = now.getFullYear();
    const day    = now.getDate();

    setMarkingToday(true);
    try {
      const token = localStorage.getItem('crm_token');
      await axios.put(`${API}/hr/monthly-attendance/update-day`, {
        employeeId: empId, month, year, day, status: 'P',
      }, { headers: { Authorization: `Bearer ${token}` } });

      // Update local state so grid reflects change immediately
      setEditedData(prev => {
        const updated = { ...prev };
        if (!updated[empId]) updated[empId] = {};
        updated[empId][String(day)] = 'P';
        return updated;
      });
      // Switch to current month view so the user can see the marked cell
      setSelectedMonth(month);
      setSelectedYear(year);
      toast.success(`Marked Present for today — ${todayDOW}, ${day} ${MONTH_NAMES[month - 1]} ${year}`);
    } catch (err) {
      toast.error('Failed to mark attendance. Please try again.');
    } finally {
      setMarkingToday(false);
    }
  }, [user, isAttendanceAdmin, now, todayDOW]); // eslint-disable-line

  const handleKbInput = useCallback((val) => {
    if (!kbCell) return;
    const upper = val.toUpperCase().replace(/[^A-Z]/g, '');
    if (!upper) return;

    // Build new buffer: only last 2 chars matter
    const newBuf = upper.slice(-2);
    setKbBuffer(newBuf);
    if (kbTimerRef.current) clearTimeout(kbTimerRef.current);

    const isExact = ALL_CODES.includes(newBuf);
    const canExtend = ALL_CODES.some(c => c.startsWith(newBuf) && c.length > newBuf.length);

    if (isExact && !canExtend) {
      // Unique complete match (H, L, A, U, WO, PL) — save instantly
      handleStatusSelect(kbCell.empId, kbCell.day, newBuf);
      setKbCell(null);
      setKbBuffer('');
    } else if (isExact && canExtend) {
      // Could extend (P → PL) — wait 700ms, then save current
      kbTimerRef.current = setTimeout(() => {
        handleStatusSelect(kbCell.empId, kbCell.day, newBuf);
        setKbCell(null);
        setKbBuffer('');
      }, 700);
    }
    // else partial match (W → WO), just update buffer and wait
  }, [kbCell, handleStatusSelect]);

  // Close kb input on Escape / click outside
  useEffect(() => {
    if (!kbCell) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (kbTimerRef.current) clearTimeout(kbTimerRef.current);
        setKbCell(null);
        setKbBuffer('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [kbCell]);

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('crm_token');
      const payload = records.map(r => ({ ...r, days: editedData[r.employeeId] || r.days }));
      await axios.put(`${API}/hr/monthly-attendance/bulk-save`, payload, {
        params: { month: selectedMonth, year: selectedYear },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Attendance saved successfully!');
      setEditMode(false);
      fetchAttendance();
    } catch (err) {
      toast.error('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const [clearDayFor, setClearDayFor] = useState(null); // {employeeId, name}
  const [clearDayInput, setClearDayInput] = useState('');

  const handleClearDay = async () => {
    const day = parseInt(clearDayInput);
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    if (!day || day < 1 || day > daysInMonth) {
      toast.error(`Enter a valid day between 1 and ${daysInMonth}`);
      return;
    }
    try {
      const token = localStorage.getItem('crm_token');
      await axios.put(`${API}/hr/monthly-attendance/update-day`, {
        employeeId: clearDayFor.employeeId,
        month: selectedMonth,
        year: selectedYear,
        day,
        status: '',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Day ${day} cleared for ${clearDayFor.name}`);
      setClearDayFor(null);
      setClearDayInput('');
      fetchAttendance();
    } catch {
      toast.error('Failed to clear day');
    }
  };

  const handleCancelEdit = () => {
    const data = {};
    records.forEach(r => { data[r.employeeId] = { ...r.days }; });
    setEditedData(data);
    setEditMode(false);
    setActiveCell(null);
  };

  const handleDeleteRow = async (employeeId) => {
    // Repurposed: now triggers per-day clear UI — see clearDayFor state
    const rec = records.find(r => r.employeeId === employeeId);
    setClearDayFor({ employeeId, name: rec?.employeeName || employeeId });
    setClearDayInput('');
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    setImportResult(null);
    try {
      const token = localStorage.getItem('crm_token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/hr/monthly-attendance/import-excel`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const { imported, month: m, year: y, unmatched_names } = res.data;
      setImportResult({ imported, month: m, year: y, unmatched: unmatched_names || [] });
      toast.success(`Imported ${imported} employee records for ${MONTH_NAMES[m - 1]} ${y}`);
      setSelectedMonth(m);
      setSelectedYear(y);
      setTimeout(fetchAttendance, 400);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const prevMonth = () => {
    if (selectedMonth === 1) { setSelectedMonth(12); setSelectedYear(y => y - 1); }
    else setSelectedMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (selectedMonth === 12) { setSelectedMonth(1); setSelectedYear(y => y + 1); }
    else setSelectedMonth(m => m + 1);
  };

  const monthOptions = [];
  for (let i = 0; i < 24; i++) {
    let m = now.getMonth() + 1 - i;
    let y = now.getFullYear();
    while (m <= 0) { m += 12; y--; }
    monthOptions.push({ month: m, year: y, label: `${MONTH_NAMES[m - 1]} ${y}` });
  }

  const getCellStatus = (empId, day) => {
    return editedData[empId]?.[day] || records.find(r => r.employeeId === empId)?.days?.[day] || '';
  };

  const PERIOD_BTNS = [
    { id: 'daily', label: 'Daily' },
    { id: 'weekly', label: 'Weekly' },
    { id: 'monthly', label: 'Monthly' },
    { id: 'yearly', label: 'Yearly' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="monthly-attendance-tab">

      {/* ===== SUMMARY BOXES ===== */}
      <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="attendance-summary-section">
        {/* Period toggle */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide mr-1">View:</span>
          {PERIOD_BTNS.map(pb => (
            <button
              key={pb.id}
              onClick={() => setSummaryPeriod(pb.id)}
              data-testid={`period-${pb.id}-btn`}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                summaryPeriod === pb.id
                  ? 'bg-stone-800 text-white border-stone-800 shadow-sm'
                  : 'bg-white text-stone-500 border-stone-200 hover:border-stone-400'
              }`}
            >
              {pb.label}
            </button>
          ))}
          <span className="ml-auto text-xs text-stone-400 italic">
            {summaryPeriod === 'daily' && `Today — ${DAY_NAMES[getDayOfWeek(selectedYear, selectedMonth, periodDays[0])]} ${periodDays[0]} ${MONTH_NAMES[selectedMonth - 1]}`}
            {summaryPeriod === 'weekly' && `Week: Days ${periodDays[0]}–${periodDays[periodDays.length - 1]}, ${MONTH_NAMES[selectedMonth - 1]}`}
            {summaryPeriod === 'monthly' && `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear} — All ${totalDays} days`}
            {summaryPeriod === 'yearly' && `Year ${selectedYear} — All months`}
          </span>
        </div>

        {/* Color code count boxes */}
        {yearlyLoading ? (
          <div className="flex items-center gap-2 text-xs text-stone-400">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-stone-400" />
            Loading yearly data...
          </div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="attendance-code-boxes">
            {ALL_CODES.map(code => {
              const cfg = STATUS_COLORS[code];
              const count = codeCounts[code] || 0;
              return (
                <div
                  key={code}
                  data-testid={`code-box-${code}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border-2 shadow-sm min-w-[110px]"
                  style={{
                    backgroundColor: cfg.bg,
                    borderColor: 'rgba(0,0,0,0.15)',
                  }}
                >
                  <div className="flex-1">
                    <div className="text-xs font-bold leading-none" style={{ color: cfg.text }}>
                      {code} = {cfg.label}
                    </div>
                    <div className="text-2xl font-extrabold leading-tight mt-0.5" style={{ color: cfg.text }}>
                      {count}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ===== MARK PRESENT TODAY — one-tap shortcut for non-admin employees ===== */}
      {!isAttendanceAdmin && (() => {
        const empId = user?.employeeId || user?.email || 'self';
        const todayStatus = editedData[empId]?.[String(todayDay)] ||
          records.find(r => r.employeeId === empId)?.days?.[String(todayDay)] || '';
        const alreadyMarked = !!todayStatus;
        const cfg = alreadyMarked ? STATUS_COLORS[todayStatus] || STATUS_COLORS.P : null;

        return (
          <div
            data-testid="mark-present-today-card"
            className="rounded-xl border-2 overflow-hidden"
            style={{
              background: alreadyMarked
                ? `linear-gradient(135deg, ${cfg.bg}22 0%, ${cfg.bg}44 100%)`
                : 'linear-gradient(135deg, #0f4c2a 0%, #166534 60%, #15803d 100%)',
              borderColor: alreadyMarked ? cfg.bg : '#16a34a',
            }}
          >
            <div className="flex flex-col sm:flex-row items-center gap-4 px-5 py-4">
              {/* Left: date info */}
              <div className="flex items-center gap-3 flex-1">
                <div
                  className="w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 shadow-md"
                  style={{ background: alreadyMarked ? cfg.bg : '#16a34a' }}
                >
                  <span className="text-2xl font-black leading-none" style={{ color: alreadyMarked ? cfg.text : '#fff' }}>
                    {todayDay}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: alreadyMarked ? cfg.text : '#bbf7d0' }}>
                    {todayDOW}
                  </span>
                </div>
                <div>
                  <p className="font-extrabold text-sm" style={{ color: alreadyMarked ? '#1f2937' : '#fff' }}>
                    {alreadyMarked
                      ? `Already marked — ${cfg ? ALL_CODES.find(c => c === todayStatus) : todayStatus} (${STATUS_COLORS[todayStatus]?.label || todayStatus})`
                      : `Today — ${todayDOW}, ${MONTH_NAMES[now.getMonth()]} ${todayDay}`}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: alreadyMarked ? '#6b7280' : '#bbf7d0' }}>
                    {alreadyMarked
                      ? 'Tap to change your attendance status for today'
                      : 'Mark yourself present for today with one tap'}
                  </p>
                </div>
              </div>

              {/* Right: action button */}
              <div className="flex gap-2 shrink-0">
                {alreadyMarked ? (
                  /* Allow changing status via a small dropdown */
                  <div className="relative group">
                    <button
                      data-testid="change-today-status-btn"
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-bold text-sm border-2 transition-all hover:opacity-90"
                      style={{ borderColor: cfg.bg, background: cfg.bg, color: cfg.text }}
                      onClick={() => setActiveCell(prev =>
                        prev?.empId === empId && prev?.day === todayDay ? null : { empId, day: todayDay }
                      )}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Change Status
                    </button>
                    {activeCell?.empId === empId && activeCell?.day === todayDay && (
                      <div
                        className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden"
                        style={{ minWidth: 160 }}
                        onClick={e => e.stopPropagation()}
                      >
                        <div className="px-3 py-2 text-xs font-bold text-gray-500 border-b bg-gray-50">
                          Change today ({todayDOW} {todayDay})
                        </div>
                        {ALL_CODES.map(code => {
                          const c = STATUS_COLORS[code];
                          return (
                            <button
                              key={code}
                              className="w-full text-left px-3 py-2 text-xs font-bold flex items-center gap-2 hover:opacity-80 transition-opacity"
                              style={{ backgroundColor: c.bg, color: c.text }}
                              onClick={() => { handleStatusSelect(empId, todayDay, code); setActiveCell(null); }}
                              data-testid={`today-status-${code}`}
                            >
                              <span className="w-6">{code}</span>
                              <span className="font-normal opacity-80">{c.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    data-testid="mark-present-today-btn"
                    onClick={handleMarkPresentToday}
                    disabled={markingToday}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-extrabold text-sm transition-all active:scale-95 disabled:opacity-60 shadow-lg hover:shadow-xl"
                    style={{
                      background: '#fff',
                      color: '#166534',
                      border: '2px solid #fff',
                    }}
                  >
                    {markingToday ? (
                      <>
                        <div className="w-4 h-4 rounded-full border-2 border-green-600 border-t-transparent animate-spin" />
                        Marking...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        Mark Present Today
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ===== HEADER CONTROLS ===== */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border rounded-lg p-4">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <Select
            value={`${selectedMonth}-${selectedYear}`}
            onValueChange={(val) => {
              const [m, y] = val.split('-');
              setSelectedMonth(parseInt(m));
              setSelectedYear(parseInt(y));
            }}
          >
            <SelectTrigger className="w-48" data-testid="month-selector">
              <SelectValue>{MONTH_NAMES[selectedMonth - 1]} {selectedYear}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map(opt => (
                <SelectItem key={`${opt.month}-${opt.year}`} value={`${opt.month}-${opt.year}`}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={nextMonth}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
            disabled={isCurrentMonth}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-stone-600 ml-1">
            {MONTH_NAMES[selectedMonth - 1]} {selectedYear} — {totalDays} Days
          </span>
        </div>

        {/* Part 1 / 2 toggle — 1 = Full grid, 2 = Summary */}
        <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
          <button
            onClick={() => setPart(1)}
            className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${part === 1 ? 'bg-white shadow text-blue-700 border border-blue-200' : 'text-stone-500 hover:text-stone-700'}`}
            data-testid="part1-btn"
            title="Full Attendance Grid (all days)"
          >
            1
          </button>
          <button
            onClick={() => setPart(2)}
            className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${part === 2 ? 'bg-white shadow text-blue-700 border border-blue-200' : 'text-stone-500 hover:text-stone-700'}`}
            data-testid="part2-btn"
            title="Monthly Summary"
          >
            2
          </button>
        </div>

        <div className="flex items-center gap-2">
          {isAttendanceAdmin && (
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={handleImportExcel}
            data-testid="attendance-file-input"
          />
          )}
          {isAttendanceAdmin && (
          <Button
            size="sm"
            variant="outline"
            className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="import-excel-btn"
          >
            <Upload className="w-4 h-4 mr-1" />
            {importing ? 'Importing...' : 'Import Excel'}
          </Button>
          )}

          {editMode ? (
            <>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleSaveAll}
                disabled={saving}
                data-testid="save-attendance-btn"
              >
                <Save className="w-4 h-4 mr-1" />
                {saving ? 'Saving...' : (isAttendanceAdmin ? 'Save All' : 'Save My Attendance')}
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelEdit} data-testid="cancel-edit-btn">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => setEditMode(true)}
              data-testid="edit-attendance-btn"
            >
              <Edit className="w-4 h-4 mr-1" /> {isAttendanceAdmin ? 'Edit Attendance' : 'Mark My Attendance'}
            </Button>
          )}
        </div>
      </div>

      {/* ===== LEGEND ===== */}
      <div className="flex flex-wrap gap-2 text-xs">
        {ALL_CODES.map(code => {
          const cfg = STATUS_COLORS[code];
          return (
            <span
              key={code}
              className="px-2 py-1 rounded font-bold border"
              style={{ backgroundColor: cfg.bg, color: cfg.text, borderColor: 'rgba(0,0,0,0.15)' }}
            >
              {code} = {cfg.label}
            </span>
          );
        })}
      </div>

      {/* Edit hint */}
      {editMode && (
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-md flex items-center gap-2">
          <Keyboard className="w-3.5 h-3.5 shrink-0" />
          <span>
            <strong>Click on any day cell</strong> to open the status picker. Select a code: <strong>P</strong>=Present, <strong>H</strong>=Half Day, <strong>WO</strong>=Weekly Off, <strong>PL</strong>=Planned Leave, <strong>SL</strong>=Sick Leave, <strong>HO</strong>=Holiday, <strong>L</strong>=Late Mark, <strong>A</strong>=Absentee, <strong>U</strong>=Unplanned Leave.
            &nbsp; Click the same cell again to close the picker.
          </span>
        </div>
      )}

      {/* Import result banner */}
      {importResult && (
        <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-md text-sm" data-testid="import-result">
          <FileSpreadsheet className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <span className="font-semibold">{importResult.imported} records</span> imported for {MONTH_NAMES[importResult.month - 1]} {importResult.year}.
            {importResult.unmatched.length > 0 && (
              <span className="ml-2 text-amber-700">
                <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                {importResult.unmatched.length} unmatched: {importResult.unmatched.join(', ')}
              </span>
            )}
          </div>
          <button onClick={() => setImportResult(null)} className="ml-auto shrink-0 text-emerald-500 hover:text-emerald-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ===== PART 1: FULL ATTENDANCE GRID (all days, horizontal scroll) ===== */}
      {part === 1 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="min-w-max text-xs border-collapse" data-testid="attendance-grid">
            <thead>
              <tr className="bg-[#CCC0DA]">
                <th className="border border-gray-300 px-1 py-1.5 text-left font-semibold text-gray-700 sticky left-0 bg-[#CCC0DA] z-10 w-10 min-w-[40px] text-xs">No</th>
                <th className="border border-gray-300 px-2 py-1.5 text-left font-semibold text-gray-700 sticky left-10 bg-[#CCC0DA] z-10 min-w-[160px] text-xs">Name &amp; Designation</th>
                {days.map(d => {
                  const dow = getDayOfWeek(selectedYear, selectedMonth, d);
                  const isWeekend = dow === 0 || dow === 6;
                  return (
                    <th
                      key={d}
                      className="border border-gray-300 py-1 font-semibold text-center w-[52px] min-w-[52px]"
                      style={{ backgroundColor: isWeekend ? '#d0e8f5' : '#CCC0DA', color: '#333' }}
                    >
                      <div className="text-[10px] font-medium">{DAY_NAMES[dow]}</div>
                      <div className="text-sm font-bold leading-tight">{d}</div>
                    </th>
                  );
                })}
                {editMode && <th className="border border-gray-300 px-1 py-1.5 text-center bg-[#CCC0DA] z-10 min-w-[40px] text-xs sticky right-0">Del</th>}
              </tr>
            </thead>
            <tbody>
              {records.map((rec, idx) => (
                <React.Fragment key={rec.employeeId}><tr
                  className="hover:bg-gray-50"
                  style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9fb' }}
                >
                  <td className="border border-gray-200 px-1 py-1.5 text-center font-medium text-gray-600 sticky left-0 z-10 bg-[#B8CCE4] w-10 min-w-[40px] text-xs">
                    {idx + 1}
                  </td>
                  <td className="border border-gray-200 px-2 py-1 font-medium text-gray-800 sticky left-10 z-10 bg-[#B8CCE4] min-w-[160px] max-w-[160px] text-xs" title={`${rec.employeeName} — ${rec.designation || ''}`}>
                    <div className="truncate font-semibold leading-tight">{rec.employeeName || rec.employeeId}</div>
                    {rec.designation && <div className="truncate text-[9px] text-gray-600 font-normal leading-tight">{rec.designation}</div>}
                  </td>

                  {days.map(day => {
                    const status = getCellStatus(rec.employeeId, day);
                    const colorCfg = STATUS_COLORS[status] || null;
                    const isActive = activeCell?.empId === rec.employeeId && activeCell?.day === day;

                    return (
                      <td
                        key={day}
                        className="border border-gray-200 px-0 py-0 text-center cursor-pointer relative"
                        style={{ backgroundColor: colorCfg ? colorCfg.bg : '#fff', width: '52px', minWidth: '52px' }}
                        onClick={() => handleCellClick(rec.employeeId, day)}
                        data-testid={`cell-${rec.employeeId}-${day}`}
                      >
                        {/* Keyboard input mode */}
                        {kbCell?.empId === rec.employeeId && kbCell?.day === day ? (
                          <input
                            autoFocus
                            maxLength={2}
                            value={kbBuffer}
                            onChange={e => handleKbInput(e.target.value)}
                            onBlur={() => {
                              if (kbTimerRef.current) clearTimeout(kbTimerRef.current);
                              setKbCell(null);
                              setKbBuffer('');
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Tab') {
                                e.preventDefault();
                                const codeToSave = ALL_CODES.includes(kbBuffer) ? kbBuffer : null;
                                if (kbTimerRef.current) clearTimeout(kbTimerRef.current);
                                const { empId: curEmpId, day: curDay } = kbCell;
                                if (codeToSave) {
                                  handleStatusSelect(curEmpId, curDay, codeToSave);
                                }
                                setKbCell(null);
                                setKbBuffer('');
                                const maxDays = getDaysInMonth(selectedYear, selectedMonth);
                                const nextDay = e.shiftKey ? curDay - 1 : curDay + 1;
                                if (nextDay >= 1 && nextDay <= maxDays) {
                                  setTimeout(() => {
                                    setKbBuffer('');
                                    setKbCell({ empId: curEmpId, day: nextDay });
                                  }, 30);
                                }
                              } else if (e.key === 'Enter' && ALL_CODES.includes(kbBuffer)) {
                                if (kbTimerRef.current) clearTimeout(kbTimerRef.current);
                                handleStatusSelect(kbCell.empId, kbCell.day, kbBuffer);
                                setKbCell(null);
                                setKbBuffer('');
                              }
                            }}
                            className="absolute inset-0 w-full h-full text-center font-bold uppercase bg-blue-100 border-2 border-blue-500 outline-none z-30"
                            style={{ fontSize: '11px', caretColor: 'transparent' }}
                            data-testid={`kb-input-${rec.employeeId}-${day}`}
                          />
                        ) : (
                          <span
                            className="block px-1 py-1.5 font-bold text-center select-none"
                            style={{ color: colorCfg ? colorCfg.text : '#999', fontSize: '11px' }}
                          >
                            {status || ''}
                          </span>
                        )}

                        {/* Dropdown (single-click) */}
                        {isActive && editMode && !kbCell && (
                          <div
                            className="absolute z-50 bg-white border border-gray-300 rounded-lg shadow-xl"
                            style={{ top: '100%', left: '50%', transform: 'translateX(-50%)', minWidth: '120px' }}
                            onClick={e => e.stopPropagation()}
                          >
                            <div className="p-1.5 text-xs font-semibold text-gray-500 border-b">Day {day}</div>
                            {ALL_CODES.map(code => {
                              const cfg = STATUS_COLORS[code];
                              return (
                                <button
                                  key={code}
                                  className="w-full text-left px-2 py-1.5 text-xs font-bold flex items-center gap-2 hover:opacity-80 transition-opacity"
                                  style={{ backgroundColor: cfg.bg, color: cfg.text }}
                                  onClick={() => handleStatusSelect(rec.employeeId, day, code)}
                                >
                                  <span className="w-6">{code}</span>
                                  <span className="font-normal opacity-80 text-[10px]">{cfg.label}</span>
                                </button>
                              );
                            })}
                            <button
                              className="w-full text-left px-2 py-1.5 text-xs text-gray-500 border-t hover:bg-gray-50"
                              onClick={() => handleStatusSelect(rec.employeeId, day, '')}
                            >
                              Clear
                            </button>
                            <button
                              className="w-full text-left px-2 py-1.5 text-xs text-red-500 border-t hover:bg-red-50"
                              onClick={() => setActiveCell(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </td>
                    );
                  })}
                  {/* Delete row button in edit mode */}
                  {editMode && (
                    <td className="border border-gray-200 px-1 py-1 text-center sticky right-0 bg-white z-10" style={{minWidth:40}}>
                      {(isAttendanceAdmin || (user?.employeeId && rec.employeeId === user.employeeId)) && (
                        <button
                          onClick={() => handleDeleteRow(rec.employeeId)}
                          className={`p-1 rounded text-white transition-colors ${clearDayFor?.employeeId === rec.employeeId ? 'bg-orange-500 ring-2 ring-orange-300' : 'bg-red-400 hover:bg-red-600'}`}
                          title="Clear a specific day's attendance"
                          data-testid={`delete-attendance-row-${rec.employeeId}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
                {/* Inline clear-day row */}
                {editMode && clearDayFor?.employeeId === rec.employeeId && (
                  <tr key={`clear-day-${rec.employeeId}`}>
                    <td colSpan={days.length + (editMode ? 3 : 2)} style={{padding:0, background:'#FFF7ED', borderBottom:'2px solid #FB923C'}}>
                      <div className="flex items-center gap-3 px-4 py-2 flex-wrap">
                        <span className="text-xs font-bold text-orange-700">
                          Clear specific day for <span className="text-orange-900">{clearDayFor.name}</span>:
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-600">Day:</span>
                          <input
                            type="number"
                            min={1}
                            max={new Date(selectedYear, selectedMonth, 0).getDate()}
                            value={clearDayInput}
                            onChange={e => setClearDayInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleClearDay(); if (e.key === 'Escape') { setClearDayFor(null); setClearDayInput(''); } }}
                            autoFocus
                            placeholder={`1–${new Date(selectedYear, selectedMonth, 0).getDate()}`}
                            className="w-20 text-xs border border-orange-300 rounded px-2 py-1 outline-none focus:ring-2 focus:ring-orange-400"
                            data-testid="clear-day-input"
                          />
                        </div>
                        <button
                          onClick={handleClearDay}
                          className="text-xs bg-orange-500 hover:bg-orange-600 text-white font-semibold px-3 py-1 rounded transition-colors"
                          data-testid="clear-day-confirm"
                        >
                          Clear Day
                        </button>
                        <button
                          onClick={() => { setClearDayFor(null); setClearDayInput(''); }}
                          className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1"
                        >
                          Cancel
                        </button>
                        <span className="text-[11px] text-orange-500 italic">
                          e.g. enter 12 to clear June 12
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}

              {records.length === 0 && (
                <tr>
                  <td colSpan={days.length + (editMode ? 3 : 2)} className="px-4 py-10 text-center text-gray-400">
                    No attendance records. Click "Edit Attendance" to add records.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== PART 2: MONTHLY SUMMARY TABLE ===== */}
      {part === 2 && records.length > 0 && (
        <div className="mt-2">
          <h3 className="text-sm font-semibold text-stone-700 mb-2">
            Monthly Summary — {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
          </h3>
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-[#CCC0DA]">
                  <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-gray-700 w-8">No</th>
                  <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-gray-700 min-w-[140px]">Employee Name</th>
                  <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-gray-700 min-w-[150px]">Designation</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700" style={{ backgroundColor: '#219235', color: '#fff' }} title="Present + Half Day">P+H</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700" style={{ backgroundColor: '#F7931E', color: '#fff' }} title="Absent">A</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700" style={{ backgroundColor: '#FF0000', color: '#fff' }} title="Unplanned Leave">UL</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700" style={{ backgroundColor: '#FFFF00', color: '#333' }} title="Half Day">HD</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700" style={{ backgroundColor: '#29B6F6', color: '#333' }} title="Weekly Off">WO</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700" style={{ backgroundColor: '#8fcc5e', color: '#1f3d12' }} title="Late Mark">LM</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700" style={{ backgroundColor: '#29B6F6', color: '#333' }} title="Planned Leave">PL</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700" style={{ backgroundColor: '#29B6F6', color: '#333' }} title="Sick Leave">SL</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700" style={{ backgroundColor: '#1565C0', color: '#fff' }} title="Holiday">HO</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700">Remark</th>
                </tr>
              </thead>
              <tbody>
                {records.map((rec, idx) => {
                  const days_data = editedData[rec.employeeId] || rec.days || {};
                  const s = calcSummary(days_data);
                  const ok = s.absent === 0 && s.unplanned === 0;
                  return (
                    <tr key={rec.employeeId} className="hover:bg-stone-50" style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9fb' }}>
                      <td className="border border-gray-200 px-2 py-1.5 text-center text-stone-500">{idx + 1}</td>
                      <td className="border border-gray-200 px-2 py-1.5 font-medium text-stone-800">{rec.employeeName}</td>
                      <td className="border border-gray-200 px-2 py-1.5 text-stone-600 text-[11px]">{rec.designation || ''}</td>
                      <td className="border border-gray-200 px-2 py-1.5 text-center font-bold text-emerald-700 bg-emerald-50">{s.present + s.halfDay}</td>
                      <td className="border border-gray-200 px-2 py-1.5 text-center font-bold text-red-700 bg-red-50">{s.absent}</td>
                      <td className="border border-gray-200 px-2 py-1.5 text-center font-bold bg-purple-50" style={{ color: '#7030A0' }}>{s.unplanned}</td>
                      <td className="border border-gray-200 px-2 py-1.5 text-center font-bold text-yellow-700 bg-yellow-50">{s.halfDay}</td>
                      <td className="border border-gray-200 px-2 py-1.5 text-center font-bold text-blue-700 bg-blue-50">{s.weeklyOff}</td>
                      <td className="border border-gray-200 px-2 py-1.5 text-center font-bold bg-orange-50" style={{ color: '#FF6600' }}>{s.lateMarkCount}</td>
                      <td className="border border-gray-200 px-2 py-1.5 text-center font-bold text-amber-700 bg-amber-50">{s.plannedLeave}</td>
                      <td className="border border-gray-200 px-2 py-1.5 text-center font-bold bg-sky-50" style={{ color: '#1e3a5f' }}>{s.sickLeave}</td>
                      <td className="border border-gray-200 px-2 py-1.5 text-center font-bold" style={{ backgroundColor: '#dbeafe', color: '#1565C0' }}>{s.holiday}</td>
                      <td className="border border-gray-200 px-2 py-1.5 text-center font-bold text-xs" style={{ color: ok ? '#00B050' : '#FF0000' }}>
                        {ok ? 'OK' : 'Review'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {part === 2 && records.length === 0 && (
        <div className="text-center py-10 text-stone-400 text-sm">
          No records for {MONTH_NAMES[selectedMonth - 1]} {selectedYear}.
        </div>
      )}
    </div>
  );
}
